<script lang="ts">
	import { page } from '$app/stores';
	import { getLocale } from '$lib/paraglide/runtime';
	import { formatPageTitle } from '$lib/utils/worktree';
	import type { SeoProps } from './Seo.types';

	let {
		title,
		description,
		canonicalPath,
		ogImage,
		ogType = 'website',
		twitterCard = 'summary_large_image',
		noindex = false,
		jsonLd,
		hreflang
	}: SeoProps = $props();

	// Map paraglide locale codes to OpenGraph locale codes (BCP 47-ish).
	// Bare code is a safe fallback for anything not mapped here.
	const OG_LOCALE_MAP: Record<string, string> = {
		en: 'en_US',
		pl: 'pl_PL'
	};
	const toOgLocale = (locale: string) => OG_LOCALE_MAP[locale] ?? locale;

	let formattedTitle = $derived(formatPageTitle(title));
	let origin = $derived($page.url.origin);
	let currentLocale = $derived(getLocale());
	let canonicalUrl = $derived(
		canonicalPath ? `${origin}${canonicalPath}` : `${origin}${$page.url.pathname}`
	);
	let resolvedOgImage = $derived(
		ogImage ?? (currentLocale === 'en' ? '/og-image-en.png' : '/og-image.png')
	);
	let ogImageUrl = $derived(
		resolvedOgImage.startsWith('http') ? resolvedOgImage : `${origin}${resolvedOgImage}`
	);
	let englishHref = $derived.by(() => {
		if (!hreflang) return canonicalUrl;
		const englishEntry = Object.entries(hreflang).find(
			([lang]) => lang === 'en' || lang.startsWith('en')
		);
		return englishEntry ? `${origin}${englishEntry[1]}` : canonicalUrl;
	});

	let ogLocale = $derived(toOgLocale(currentLocale));
	// Alternate OG locales = every OTHER locale present in the hreflang prop.
	let ogLocaleAlternates = $derived.by(() => {
		if (!hreflang) return [];
		return Object.keys(hreflang)
			.filter((lang) => lang !== currentLocale && lang !== 'x-default')
			.map(toOgLocale);
	});
</script>

<svelte:head>
	<title>{formattedTitle}</title>
	<meta name="description" content={description} />
	<link rel="canonical" href={canonicalUrl} />

	{#if noindex}
		<meta name="robots" content="noindex, nofollow" />
	{/if}

	<!-- hreflang -->
	{#if hreflang}
		{#each Object.entries(hreflang) as [lang, path]}
			<link rel="alternate" hreflang={lang} href="{origin}{path}" />
		{/each}
		<link rel="alternate" hreflang="x-default" href={englishHref} />
	{/if}

	<!-- Open Graph -->
	<meta property="og:title" content={title} />
	<meta property="og:description" content={description} />
	<meta property="og:type" content={ogType} />
	<meta property="og:url" content={canonicalUrl} />
	<meta property="og:image" content={ogImageUrl} />
	<!-- Dimensions/type for the default og-image.png (1200×630). -->
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:type" content="image/png" />
	<meta property="og:site_name" content="Samlla" />
	<meta property="og:locale" content={ogLocale} />
	{#each ogLocaleAlternates as altLocale}
		<meta property="og:locale:alternate" content={altLocale} />
	{/each}

	<!-- Twitter Card -->
	<meta name="twitter:card" content={twitterCard} />
	<meta name="twitter:title" content={title} />
	<meta name="twitter:description" content={description} />
	<meta name="twitter:image" content={ogImageUrl} />

	<!-- JSON-LD Structured Data -->
	{#if jsonLd}
		{@html `<script type="application/ld+json">${JSON.stringify(jsonLd)}</script>`}
	{/if}
</svelte:head>
